import React, { useCallback, useEffect, useState } from "react";
import { CheckCircle, Warning } from "@phosphor-icons/react";
import { api, formatApiError } from "../../lib/api";
import { useT } from "../../lib/i18n";

const AUDIT_ITEMS = [
  { id: "prrc" },
  { id: "roles_responsibilities" },
  { id: "facility_authorization" },
  { id: "premises_suitability" },
  { id: "environmental_conditions" },
  { id: "cleaning_hygiene" },
  { id: "pest_contamination" },
  { id: "physical_access" },
  { id: "supplier_qualification" },
  { id: "quality_agreements" },
  { id: "receiving_quarantine" },
  { id: "returns_rejections" },
  { id: "equipment_calibration" },
  { id: "staff_training" },
  { id: "records_retention" },
];

export default function CompanyAuditTab() {
  const t = useT();
  const [assessments, setAssessments] = useState({});
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [saveError, setSaveError] = useState("");
  const [savingIds, setSavingIds] = useState(() => new Set());

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError("");
    try {
      const { data } = await api.get("/user/company/audit");
      setAssessments(data.assessments || {});
    } catch (error) {
      setLoadError(formatApiError(error.response?.data?.detail) || t("company.audit.save_error"));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    load();
  }, [load]);

  async function setAssessment(itemId, assessment) {
    setSaveError("");
    const previous = assessments[itemId] || null;
    setAssessments((current) => ({ ...current, [itemId]: assessment }));
    setSavingIds((current) => new Set(current).add(itemId));
    try {
      const { data } = await api.put("/user/company/audit", { assessments: { [itemId]: assessment } });
      setAssessments((current) => ({ ...current, [itemId]: data.assessments?.[itemId] || null }));
    } catch (error) {
      setAssessments((current) => ({ ...current, [itemId]: previous }));
      setSaveError(formatApiError(error.response?.data?.detail) || t("company.audit.error"));
    } finally {
      setSavingIds((current) => {
        const next = new Set(current);
        next.delete(itemId);
        return next;
      });
    }
  }

  const completedCount = AUDIT_ITEMS.filter((item) => assessments[item.id] === "ok" || assessments[item.id] === "review").length;
  const reviewCount = AUDIT_ITEMS.filter((item) => assessments[item.id] === "review").length;
  const allComplete = completedCount === AUDIT_ITEMS.length;

  if (loading) {
    return <div className="grid-card p-6 text-sm text-zinc-500" data-testid="company-audit-loading">{t("company.audit.loading")}</div>;
  }

  if (loadError) {
    return (
      <div className="grid-card p-6 space-y-3" data-testid="company-audit-load-error">
        <p className="text-sm text-red-700">{loadError}</p>
        <button onClick={load} className="btn-primary px-3 py-2 text-sm">{t("app.retry")}</button>
      </div>
    );
  }

  return (
    <section className="space-y-4" data-testid="company-audit">
      <header className="grid-card p-5">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <h2 className="text-lg font-extrabold tracking-tight">{t("company.audit.title")}</h2>
            <p className="mt-1 text-sm text-zinc-600">{t("company.audit.sub")}</p>
          </div>
          <div
            className={`facility-audit-status inline-flex shrink-0 flex-wrap items-center gap-2 rounded-sm border px-3 py-2 text-sm font-bold ${allComplete ? "facility-audit-status--complete" : "facility-audit-status--pending"}`}
            data-testid="company-audit-status"
            role="status"
          >
            {allComplete ? <CheckCircle size={18} weight="fill" /> : <Warning size={18} weight="fill" />}
            {allComplete ? t("company.audit.complete") : t("company.audit.pending")}
            {allComplete && reviewCount > 0 && (
              <span className="facility-audit-review-count">{reviewCount} {t("company.audit.review_count")}</span>
            )}
          </div>
        </div>
        <div className="mt-4 flex items-center gap-3">
          <div className="facility-audit-progress-track h-2 flex-1 overflow-hidden rounded-full" aria-hidden="true">
            <div
              className={`facility-audit-progress-fill h-full rounded-full transition-all ${allComplete ? "facility-audit-progress-fill--complete" : "facility-audit-progress-fill--pending"}`}
              style={{ width: `${(completedCount / AUDIT_ITEMS.length) * 100}%` }}
            />
          </div>
          <span className="shrink-0 text-xs font-mono text-zinc-500" data-testid="company-audit-progress">
            {completedCount}/{AUDIT_ITEMS.length} {t("company.audit.progress")}
          </span>
        </div>
      </header>

      <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
        {AUDIT_ITEMS.map((item, index) => {
          const assessment = assessments[item.id] || null;
          const title = t(`company.audit.item.${item.id}`);
          return (
            <article
              key={item.id}
              className={`facility-audit-item facility-audit-item--${assessment || "pending"} flex min-w-0 flex-col gap-3 rounded-sm border p-4 transition-colors`}
              data-assessment={assessment || "pending"}
              data-testid={`company-audit-item-${item.id}`}
            >
              <div className="flex min-w-0 items-start gap-2">
                <span className="font-mono text-xs text-zinc-400">{String(index + 1).padStart(2, "0")}</span>
                <span className="facility-audit-item-title min-w-0 flex-1 text-sm font-semibold leading-6">{title}</span>
                <span className={`facility-audit-item-status shrink-0 text-xs font-semibold facility-audit-item-status--${assessment || "pending"}`}>
                  {assessment === "ok" ? t("company.audit.ok") : assessment === "review" ? t("company.audit.needs_review") : t("company.audit.not_assessed")}
                </span>
              </div>
              <div className="facility-audit-item-detail pl-6 text-xs leading-5">
                <p><strong>{t("company.audit.applies_label")}</strong> {t(`company.audit.applies.${item.id}`)}</p>
                <p className="mt-1"><strong>{t("company.audit.basis_label")}</strong> {t(`company.audit.basis.${item.id}`)}</p>
              </div>
              <div className="flex flex-wrap items-center gap-2 pl-6">
                <button
                  type="button"
                  disabled={savingIds.has(item.id)}
                  aria-label={`${t("company.audit.set_ok")}: ${title}`}
                  aria-pressed={assessment === "ok"}
                  data-testid={`company-audit-choice-ok-${item.id}`}
                  onClick={() => setAssessment(item.id, assessment === "ok" ? null : "ok")}
                  className={`facility-audit-choice facility-audit-choice--ok ${assessment === "ok" ? "facility-audit-choice--selected" : ""}`}
                >
                  <CheckCircle size={14} weight="fill" /> {t("company.audit.ok")}
                </button>
                <button
                  type="button"
                  disabled={savingIds.has(item.id)}
                  aria-label={`${t("company.audit.set_review")}: ${title}`}
                  aria-pressed={assessment === "review"}
                  data-testid={`company-audit-choice-review-${item.id}`}
                  onClick={() => setAssessment(item.id, assessment === "review" ? null : "review")}
                  className={`facility-audit-choice facility-audit-choice--review ${assessment === "review" ? "facility-audit-choice--selected" : ""}`}
                >
                  <Warning size={14} weight="fill" /> {t("company.audit.needs_review")}
                </button>
                {savingIds.has(item.id) && <span className="text-xs text-zinc-500">{t("company.audit.saving")}</span>}
              </div>
            </article>
          );
        })}
      </div>

      {saveError && (
        <p className="rounded-sm border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700" role="alert" data-testid="company-audit-save-error">
          {saveError}
        </p>
      )}
      <p className="text-xs leading-5 text-zinc-500" data-testid="company-audit-disclaimer">
        {t("company.audit.disclaimer")}
      </p>
    </section>
  );
}
